import Close from '@mui/icons-material/Close';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { tagFilterKeys, type TagFilterKeys } from '@shared/filters';
import { useEffect, useMemo, useState } from 'react';
import { countries } from '~/scripts/countries';
import { ucfirst } from '~/scripts/ucfirst';
import { dashesToYyyymmdd, yyyymmddToDashes } from '~/scripts/yyyymmdd';
import type { Filters } from '~/types/filters';
import type { Search } from '~/types/search';
import StringFilterDropdown from './StringFilterDropdown';
import { allFilters } from './filters';
import { datasets } from '~/scenes/datasets';
interface FiltersMenuProps {
  name: string;
  isSingleReportUuid: string;
  filters: Filters.Filters;
  onChange: (filters: Filters.Filters) => void;
  facets: Search.Facets | undefined | null;
  isLoading: boolean;
}
export const FiltersMenu = ({
  filters,
  onChange,
  isSingleReportUuid,
  name,
  isLoading,
  facets,
}: FiltersMenuProps) => {
  const useReducer = (
    list: Search.FacetValue,
    currentValues: string[] | undefined
  ): string[] => {
    return useMemo(() => {
      const deduplicatedCurrentValues = (currentValues || [])?.filter(
        (value) => !list?.[value]
      );
      return [...Object.keys(list || {}), ...deduplicatedCurrentValues];
    }, [list, currentValues]);
  };

  const tldOptions = useReducer(facets?.tld, filters.tld);
  const countryOptions = useReducer(facets?.country, filters.country);
  const industriesOptions = useReducer(facets?.tags, filters.tags);
  const [updates, setUpdates] = useState<Filters.Filters>(
    JSON.parse(JSON.stringify(filters))
  );

  useEffect(() => {
    setUpdates(JSON.parse(JSON.stringify(filters)));
  }, [filters]);

  const enforcePositiveNumber = (
    value: string | undefined
  ): number | undefined => {
    const number = +(value || '');
    return isNaN(number) || number < 1 ? undefined : number;
  };

  const clearableInputAdornment = (key: keyof Filters.Filters) => {
    if (!filters[key]) {
      return null;
    }
    return (
      <InputAdornment position="end">
        <IconButton
          size="small"
          onClick={() =>
            handleChange({
              ...updates,
              [key]: undefined,
            })
          }>
          <Close fontSize="small" />
        </IconButton>
      </InputAdornment>
    );
  };

  const handleChange = (filters: Filters.Filters) => {
    onChange(filters);
  };

  return (
    <Grid container rowSpacing={0}>
      {isSingleReportUuid && (
        <>
          <Grid item xs={12}>
            <FilterSectionTitle
              filterTitle="Activity Dates"
              FilterIcon={allFilters.added.icon}
              tooltip={allFilters.added.tooltip}
              divider={false}
            />
          </Grid>
          <Grid item xs={12} mx={2}>
            <TextField
              label={`Added ${name} on or after`}
              disabled={isLoading}
              variant="outlined"
              size="small"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={
                updates[`added_${isSingleReportUuid}`]
                  ? yyyymmddToDashes(
                      updates[`added_${isSingleReportUuid}`] as string
                    )
                  : ''
              }
              onChange={(e) => {
                handleChange({
                  ...updates,
                  [`added_${isSingleReportUuid}`]:
                    dashesToYyyymmdd(e.target.value) || undefined,
                });
              }}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <FilterSectionTitle
          filterTitle="Popularity Rank"
          FilterIcon={allFilters.rank_min.icon}
          tooltip={allFilters.rank_min.tooltip}
          divider={!!isSingleReportUuid}
        />
      </Grid>
      <Grid item xs={6} pl={2}>
        <TextField
          size="small"
          label="Min. Rank"
          disabled={isLoading}
          value={updates.rank_min || ''}
          fullWidth
          variant="outlined"
          InputProps={{
            inputProps: {
              min: 1,
              max: 100000000,
            },
            endAdornment: clearableInputAdornment('rank_min'),
          }}
          type="number"
          onChange={(e) => {
            handleChange({
              ...updates,
              rank_min: enforcePositiveNumber(e.target.value),
            });
          }}
        />
      </Grid>
      <Grid item xs={6} pl={1} pr={2}>
        <TextField
          size="small"
          label="Max. Rank"
          disabled={isLoading}
          value={updates.rank_max || ''}
          fullWidth
          variant="outlined"
          InputProps={{
            inputProps: {
              min: 1,
              max: 100000000,
            },
            endAdornment: clearableInputAdornment('rank_max'),
          }}
          type="number"
          onChange={(e) => {
            handleChange({
              ...updates,
              rank_max: enforcePositiveNumber(e.target.value),
            });
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FilterSectionTitle
          filterTitle="Tech Spend"
          FilterIcon={allFilters.spend_min.icon}
          tooltip={allFilters.spend_min.tooltip}
        />
      </Grid>
      <Grid item xs={6} pl={2}>
        <TextField
          size="small"
          label="Minimum"
          disabled={isLoading}
          value={updates.spend_min || ''}
          fullWidth
          type="number"
          variant="outlined"
          InputProps={{
            startAdornment: '$',
            inputProps: {
              min: 1,
              max: 100000000,
            },
            endAdornment: clearableInputAdornment('spend_min'),
          }}
          onChange={(e) => {
            handleChange({
              ...updates,
              spend_min: enforcePositiveNumber(e.target.value),
            });
          }}
        />
      </Grid>
      <Grid item xs={6} pl={1} pr={2}>
        <TextField
          size="small"
          label="Maximum"
          disabled={isLoading}
          value={updates.spend_max || ''}
          fullWidth
          variant="outlined"
          type="number"
          InputProps={{
            startAdornment: '$',
            inputProps: {
              min: 1,
              max: 100000000,
            },
            endAdornment: clearableInputAdornment('spend_max'),
          }}
          onChange={(e) => {
            handleChange({
              ...updates,
              spend_max: enforcePositiveNumber(e.target.value),
            });
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FilterSectionTitle
          filterTitle="Employee Count"
          FilterIcon={allFilters.employees_min.icon}
          tooltip={allFilters.employees_min.tooltip}
        />
      </Grid>
      <Grid item xs={6} pl={2}>
        <TextField
          size="small"
          label="Minimum"
          disabled={isLoading}
          value={updates.employees_min || ''}
          fullWidth
          type="number"
          variant="outlined"
          InputProps={{
            inputProps: {
              min: 1,
              max: 1000000,
            },
            endAdornment: clearableInputAdornment('employees_min'),
          }}
          onChange={(e) => {
            handleChange({
              ...updates,
              employees_min: enforcePositiveNumber(e.target.value),
            });
          }}
        />
      </Grid>
      <Grid item xs={6} pl={1} pr={2}>
        <TextField
          size="small"
          label="Maximum"
          disabled={isLoading}
          value={updates.employees_max || ''}
          fullWidth
          variant="outlined"
          type="number"
          InputProps={{
            inputProps: {
              min: 1,
              max: 1000000,
            },
            endAdornment: clearableInputAdornment('employees_max'),
          }}
          onChange={(e) => {
            handleChange({
              ...updates,
              employees_max: enforcePositiveNumber(e.target.value),
            });
          }}
        />
      </Grid>

      {
        <>
          <Grid item xs={12}>
            <FilterSectionTitle
              filterTitle="Domains"
              FilterIcon={allFilters.tld.icon}
              tooltip={allFilters.tld.tooltip}
            />
          </Grid>
          <Grid item xs={12} px={2}>
            <StringFilterDropdown
              options={tldOptions}
              disabled={isLoading}
              label={`Filter Domains`}
              value={updates.tld || []}
              getOptionLabel={(label) =>
                `${label === 'missing' ? 'Other' : `.${label}`} ${
                  facets?.tld?.[label]
                    ? `(${facets?.tld?.[label].toLocaleString()})`
                    : ''
                }`
              }
              onChange={(values) => {
                handleChange({
                  ...updates,
                  tld: nonEmptyArrayOrUndefined(values),
                });
              }}
            />
          </Grid>
        </>
      }
      {
        <>
          <Grid item xs={12}>
            <FilterSectionTitle
              filterTitle="Countries"
              FilterIcon={allFilters.country.icon}
              tooltip={allFilters.country.tooltip}
            />
          </Grid>
          <Grid item xs={12} px={2}>
            <StringFilterDropdown
              options={countryOptions}
              disabled={isLoading}
              value={updates.country || []}
              label={`Filter Countries`}
              getOptionLabel={(label) =>
                `${label === 'missing' ? 'Other' : countries[label] || label} ${
                  facets?.country?.[label]
                    ? `(${facets?.country?.[label].toLocaleString()})`
                    : ''
                }`
              }
              onChange={(values) => {
                handleChange({
                  ...updates,
                  country: nonEmptyArrayOrUndefined(values),
                });
              }}
            />
          </Grid>
        </>
      }
      {
        <>
          <Grid item xs={12}>
            <FilterSectionTitle
              filterTitle="Industries"
              FilterIcon={allFilters.industries.icon}
              tooltip={allFilters.industries.tooltip}
            />
          </Grid>
          <Grid item xs={12} px={2}>
            <StringFilterDropdown
              options={industriesOptions}
              disabled={isLoading}
              label={`Filter Industries`}
              value={updates.industries || []}
              getOptionLabel={(label) =>
                `${label === 'missing' ? 'Other' : ucfirst(label)} ${
                  facets?.industries?.[label]
                    ? `(${facets?.industries?.[label].toLocaleString()})`
                    : ''
                }`
              }
              onChange={(values) => {
                handleChange({
                  ...updates,
                  industries: nonEmptyArrayOrUndefined(values),
                });
              }}
            />
          </Grid>
        </>
      }
      <Grid item xs={12}>
        <FilterSectionTitle
          filterTitle="Popular Lists"
          FilterIcon={allFilters.tags.icon}
          tooltip={allFilters.tags.tooltip}
        />
      </Grid>
      <Grid item xs={12} px={2}>
        <StringFilterDropdown
          options={tagFilterKeys}
          disabled={isLoading}
          label={`Filter Popular Lists`}
          value={updates.tags || []}
          getOptionLabel={(label) =>
            `${label === 'missing' ? 'Other' : datasets?.[label as TagFilterKeys]?.name || label} ${
              facets?.tags?.[label]
                ? `(${facets?.tags?.[label].toLocaleString()})`
                : ''
            }`
          }
          onChange={(values) => {
            handleChange({
              ...updates,
              tags: nonEmptyArrayOrUndefined(values),
            });
          }}
        />
      </Grid>
    </Grid>
  );
};

const FilterSectionTitle = ({
  filterTitle,
  FilterIcon,
  tooltip,
  divider = true,
}: {
  filterTitle: string;
  FilterIcon: typeof SvgIcon;
  tooltip: string;
  divider?: boolean;
}) => {
  return (
    <Box sx={{ mb: 1 }}>
      {divider && <Divider sx={{ mt: 2 }} />}
      <Tooltip title={tooltip} placement="right-end">
        <Typography
          variant="subtitle1"
          fontWeight={'bold'}
          sx={{
            textDecoration: 'uppercase',
            display: 'flex',
            alignItems: 'center',
          }}
          px={2}
          pt={1}
          pb={1}>
          <FilterIcon
            sx={{ verticalAlign: 'middle', mr: 1 }}
            fontSize="small"
          />
          {filterTitle}
        </Typography>
      </Tooltip>
    </Box>
  );
};

const nonEmptyArrayOrUndefined = (value: string[]): string[] | undefined => {
  return value.length ? value : undefined;
};
